// Helper label data (angka langsung di grafik) yang dipakai SEMUA grafik SIMUTU.
// Format & aturan anti-tumpuk diatur di sini, bukan per halaman.
import { useEffect, useState } from "react";
import { LabelList } from "recharts";

const NARROW_PX = 500;
const MANY_POINTS = 15;
const NEAR_TARGET = 8; // selisih (poin %) yang dianggap "dekat" garis Target
const HALO = { stroke: "#fff", strokeWidth: 3, paintOrder: "stroke", strokeLinejoin: "round" };

export function useIsNarrow() {
  const get = () => (typeof window !== "undefined" ? window.innerWidth < NARROW_PX : false);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const on = () => setNarrow(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return narrow;
}

export const fmtLabelPct = (v) => (v == null || v === "" ? "" : `${Number(v).toFixed(1)}%`);
export const fmtLabelNum = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : String(+n.toFixed(1));
};

// Margin yang cukup agar label tidak terpotong.
export const lineChartMargin = (narrow, left = 0) => ({ top: 28, right: narrow ? 72 : 84, left, bottom: 0 });

// Selang-seling: titik terakhir selalu tampil.
const showAt = (index, n, alternate) => !alternate || (n - 1 - index) % 2 === 0;

// Angka di atas tiap titik Capaian; di bawah titik bila dekat Target.
export function capaianLabels({ data, narrow, color = "hsl(var(--brand-teal))" }) {
  const n = data.length;
  const small = narrow || n > MANY_POINTS;
  const alternate = narrow && n > 6;
  return (
    <LabelList
      dataKey="Capaian"
      content={({ x, y, value, index }) => {
        if (value == null || x == null || y == null || !showAt(index, n, alternate)) return null;
        const t = data[index]?.Target;
        // Dekat Target: label menjauh dari garis (di bawah titik bila capaian < target, di atas bila >= target).
        const near = t != null && value < t && t - value <= NEAR_TARGET && value >= 10;
        const dy = near ? (small ? 15 : 18) : small ? -8 : -10;
        return (
          <text x={x} y={y + dy} textAnchor="middle" fontSize={small ? 10 : 12} fontWeight={700}
            style={{ fill: color, ...HALO }}>
            {fmtLabelPct(value)}
          </text>
        );
      }}
    />
  );
}

// Garis Target: hanya satu label di ujung kanan, mis. "Target 90.0%".
export function targetEndLabel({ data, narrow }) {
  let last = -1;
  data.forEach((d, i) => { if (d.Target != null) last = i; });
  return (
    <LabelList
      dataKey="Target"
      content={({ x, y, value, index }) => {
        if (index !== last || value == null) return null;
        return (
          <text x={x + 8} y={y} dy={4} textAnchor="start" fontSize={narrow ? 10 : 12} fontWeight={700}
            style={{ fill: "hsl(var(--brand-deep))", ...HALO }}>
            {`Target ${fmtLabelNum(value)}%`}
          </text>
        );
      }}
    />
  );
}

// Batang horizontal: angka di ujung kanan batang.
export function barEndLabel() {
  return <LabelList dataKey="jumlah" position="right" formatter={fmtLabelNum} style={{ fontSize: 12, fill: "#334155", fontWeight: 600 }} />;
}

// Batang vertikal: angka di atas batang; selang-seling bila banyak batang / layar sempit.
export function barTopLabel({ dataKey, count, narrow, fill = "#334155" }) {
  const small = narrow || count > MANY_POINTS;
  const alternate = count > 24 || (narrow && count > 8);
  return (
    <LabelList
      dataKey={dataKey}
      content={({ x, y, width, value, index }) => {
        if (value == null || x == null || !showAt(index, count, alternate)) return null;
        return (
          <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={small ? 10 : 12} fontWeight={600}
            style={{ fill, ...HALO }}>
            {fmtLabelNum(value)}
          </text>
        );
      }}
    />
  );
}

// Batang bertumpuk: angka tiap segmen (>0) di tengah segmen.
export function stackSegmentLabel({ dataKey, narrow }) {
  return (
    <LabelList
      dataKey={dataKey}
      content={({ x, y, width, height, value }) => {
        if (!value || x == null || height < (narrow ? 12 : 14)) return null;
        return (
          <text x={x + width / 2} y={y + height / 2} dy={4} textAnchor="middle" fontSize={narrow ? 10 : 12}
            fontWeight={700} fill="#fff">
            {fmtLabelNum(value)}
          </text>
        );
      }}
    />
  );
}

// Batang bertumpuk: total di atas tumpukan (hanya bila segmen terisi > 1).
export function stackTotalLabel({ series, rows, narrow }) {
  return (
    <LabelList
      dataKey={series[series.length - 1]}
      content={({ x, y, width, index }) => {
        const row = rows[index];
        if (x == null || !row) return null;
        const filled = series.filter((s) => row[s] > 0).length;
        if (filled < 2) return null;
        return (
          <text x={x + width / 2} y={y - 5} textAnchor="middle" fontSize={narrow ? 10 : 12} fontWeight={700}
            style={{ fill: "#334155", ...HALO }}>
            {fmtLabelNum(row.total)}
          </text>
        );
      }}
    />
  );
}
